import type { JSX } from 'react';
import React from 'react';

import { Waypoint } from 'react-waypoint';

import { Text } from '@change/ds-core-ui/next';

import type { StarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import {
	useStarterDashboardV2PageData,
	useStarterDashboardV2TabsStateContext,
} from 'src/app/pages/starterDashboardV2/pageContext';
import { useRecommendedGroupPickerContext } from 'src/app/pages/starterDashboardV2/shared/components';
import type {
	FacebookPost,
	FullPostType,
	PostPreviewSizes,
	RecommendedGroup,
	RecommendedGroupPickerProps,
	RedditPost,
	SharingProps,
} from 'src/app/pages/starterDashboardV2/shared/components';
import {
	extractSharingResource,
	mapToSharingModalProps,
	POST_CONFIGS,
	useMarkModuleAsLoaded,
	useReadyToShareContext,
	useRecommendations,
	useSharingModal,
} from 'src/app/pages/starterDashboardV2/shared/contexts';
import type { PostConfigRow } from 'src/app/pages/starterDashboardV2/shared/contexts';
import type { ReadyToSharePostWithPlatform } from 'src/app/shared/api/readyToShare';
import type { WaypointCallbackArgs } from 'src/app/shared/components/layout/lazy';
import { toLinearGradient } from 'src/app/shared/components/ScrollContainer';
import { useIsMobile } from 'src/app/shared/hooks/userAgent';

import { ScrollButtons } from '../shared/ScrollButtons';

import { PlatformFilter } from './components/PlatformFilter';
import { PostPreviewCard } from './components/PostPreviewCard';
import { PostPreviewCardSkeleton } from './components/PostPreviewCardSkeleton';
import {
	useClickTracking,
	useGroupSelectionTracking,
	usePlatformFilter,
	useRenderPostProps,
	useScroll,
	useViewTracking,
} from './hooks';

const trackingProps = {
	context: 'petition_dashboard_ready_to_share_post' as const,
	viewContext: 'share' as const,
};

function recommendedGroupForPost(
	fullPostType: FullPostType,
	postConfig: PostConfigRow,
	recommendationsData: ReturnType<typeof useRecommendations>['data'],
) {
	if (!postConfig.requiresRecommendedGroup) return undefined;

	const { isLoading: isRecommendationsLoading, hasFacebookGroups, hasRedditGroups } = recommendationsData;

	if (isRecommendationsLoading) return undefined;

	if (fullPostType.startsWith('facebook')) {
		return hasFacebookGroups ? recommendationsData.facebook[0] : undefined;
	}

	return hasRedditGroups ? recommendationsData.reddit[0] : undefined;
}

type RenderPostOrStoryProps = {
	post: ReadyToSharePostWithPlatform;
	fullPostType: FullPostType;
	isMobile: boolean;
	petition: StarterDashboardV2PageData['petition'];
	options: {
		previewSize: PostPreviewSizes;
		startSharingFlow: (props: SharingProps) => void;
		startGroupSelection: (
			props: Omit<RecommendedGroupPickerProps, 'isOpen' | 'onGroupSelected' | 'onClose'>,
		) => Promise<RecommendedGroup | undefined>;
		trackClick: (fullPostType: FullPostType) => void;
		trackGroupSelection: (fullPostType: FullPostType) => void;
	};
	recommendationsData: ReturnType<typeof useRecommendations>['data'];
};

function renderPostOrStory(props: RenderPostOrStoryProps) {
	const {
		post,
		fullPostType,
		isMobile,
		petition,
		recommendationsData,
		options: { startSharingFlow, startGroupSelection, previewSize, trackClick, trackGroupSelection },
	} = props;

	const postConfig = POST_CONFIGS[fullPostType];
	const postDisabled = !isMobile && !postConfig.desktopEnabled;
	const recommendedGroup = recommendedGroupForPost(fullPostType, postConfig, recommendationsData);

	const sharingResource = extractSharingResource(fullPostType, {
		post,
		petition,
		recommendedGroup,
	});

	const sharingProps = mapToSharingModalProps(petition, {
		fullPostType,
		recommendedGroup,
		trackingProps,
	});

	const handlePostClick = !postDisabled
		? async () => {
				trackClick(fullPostType);

				if (postConfig.requiresGroupSelection) {
					trackGroupSelection(fullPostType);
					const sharingPlatform = sharingProps.platform;
					const recommendedGroupsList =
						sharingPlatform === 'facebook' ? recommendationsData.facebook : recommendationsData.reddit;
					const selectedGroup = await startGroupSelection({
						platform: sharingProps.platform === 'facebook' ? ('facebook' as const) : ('reddit' as const),
						recommendedGroups: recommendedGroupsList,
					});

					if (selectedGroup) {
						startSharingFlow({ ...sharingProps, recommendedGroup: selectedGroup } as RedditPost | FacebookPost);
					}
				} else {
					startSharingFlow(sharingProps);
				}
			}
		: () => {};

	return (
		<PostPreviewCard
			key={`${fullPostType}-post-preview-card`}
			onClick={handlePostClick}
			fullPostType={fullPostType}
			disabled={postDisabled}
			petition={petition}
			sharingResource={sharingResource}
			previewSize={previewSize}
		/>
	);
}

type PostsCarouselProps = {
	displayablePosts: Array<{ post?: ReadyToSharePostWithPlatform; fullPostType: FullPostType }>;
	baseRenderPostProps: Omit<RenderPostOrStoryProps, 'post' | 'fullPostType'>;
	previewSize: PostPreviewSizes;
	petition: StarterDashboardV2PageData['petition'];
	scrollContainerRef: React.RefObject<HTMLDivElement>;
	canScrollLeft: boolean;
	canScrollRight: boolean;
};

function PostsCarousel({
	displayablePosts,
	baseRenderPostProps,
	previewSize,
	petition,
	scrollContainerRef,
	canScrollLeft,
	canScrollRight,
}: PostsCarouselProps): JSX.Element {
	return (
		<div className="relative overflow-hidden">
			<div
				className="flex w-full flex-row gap-x-[39px] overflow-x-auto px-1 pb-3 pt-4"
				ref={scrollContainerRef}
				sx={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
			>
				{displayablePosts.map((displayablePost) => {
					const { post, fullPostType } = displayablePost;

					if (post) {
						return renderPostOrStory({ ...baseRenderPostProps, post, fullPostType });
					}

					return (
						<PostPreviewCardSkeleton
							key={`${fullPostType}-post-skeleton`}
							size={previewSize}
							petition={petition}
							fullPostType={fullPostType}
						/>
					);
				})}
			</div>
			<div
				aria-hidden
				className={canScrollLeft ? 'hidden md:block' : 'hidden'}
				sx={{
					position: 'absolute',
					left: 0,
					top: 0,
					bottom: '12px',
					width: '50px',
					pointerEvents: 'none',
					zIndex: 10,
					background: toLinearGradient('#ffffff', 'left'),
				}}
			/>
			<div
				aria-hidden
				className={canScrollRight ? 'hidden md:block' : 'hidden'}
				sx={{
					position: 'absolute',
					right: 0,
					top: 0,
					bottom: '12px',
					width: '50px',
					pointerEvents: 'none',
					zIndex: 10,
					background: toLinearGradient('#ffffff', 'right'),
				}}
			/>
		</div>
	);
}

// eslint-disable-next-line max-statements -- Function requires multiple hooks and state management for comprehensive dashboard functionality
export function ReadyToSharePreDrafted(): JSX.Element | null {
	const isMobile = useIsMobile();
	const {
		data: { petition },
	} = useStarterDashboardV2PageData();
	const {
		data: { selectedTab },
	} = useStarterDashboardV2TabsStateContext();
	const { data: contextData } = useReadyToShareContext();
	const { data: recommendationsData } = useRecommendations();

	const { startGroupSelection } = useRecommendedGroupPickerContext();
	const { startSharingFlow } = useSharingModal();

	const {
		data: { selectedPlatform, hasAnyValidContent, availablePlatforms, displayablePosts },
		actions: { selectPlatform, clearPlatformSelection },
	} = usePlatformFilter(contextData.platformData, recommendationsData, petition);

	const {
		data: { canScrollLeft, canScrollRight, scrollContainerRef },
		actions: { handleScroll },
	} = useScroll(selectedPlatform);
	const { trackView } = useViewTracking({ selectedTab });
	const { trackClick } = useClickTracking({ selectedTab });
	const { trackGroupSelection } = useGroupSelectionTracking({ selectedTab });

	const previewSize = (isMobile ? 'medium' : 'large') as PostPreviewSizes;

	const baseRenderPostProps = useRenderPostProps({
		petition,
		isMobile,
		recommendationsData,
		previewSize,
		startSharingFlow,
		startGroupSelection,
		trackClick,
		trackGroupSelection,
	});

	useMarkModuleAsLoaded('ReadyToSharePreDrafted', contextData.shouldShowReadyToShareExperiment);

	if (!contextData.shouldShowReadyToShareExperiment) {
		return null;
	}

	// Hide module if no valid content is available and not loading
	if (!hasAnyValidContent) {
		return null;
	}

	return (
		<section
			id="ready-to-share-posts"
			className="bg-background-base flex h-full w-full flex-col px-4 py-8 md:px-12 md:pb-12 md:pt-8 lg:py-12"
		>
			<Waypoint onPositionChange={(wp: WaypointCallbackArgs) => trackView(wp.currentPosition)} />

			<div className="mb-5">
				<Text as="h3" className="text-size-heading-small">
					Ready-to-share posts
				</Text>
			</div>
			<div className="flex justify-between align-middle">
				<PlatformFilter
					selectedPlatform={selectedPlatform}
					onClearSelection={clearPlatformSelection}
					onPlatformSelect={selectPlatform}
					availablePlatforms={availablePlatforms}
				/>
				{/* Scroll Buttons */}
				<ScrollButtons
					canScrollLeft={canScrollLeft}
					canScrollRight={canScrollRight}
					onScrollLeft={() => handleScroll('left')}
					onScrollRight={() => handleScroll('right')}
				/>
			</div>

			<PostsCarousel
				displayablePosts={displayablePosts}
				baseRenderPostProps={baseRenderPostProps}
				previewSize={previewSize}
				petition={petition}
				scrollContainerRef={scrollContainerRef}
				canScrollLeft={canScrollLeft}
				canScrollRight={canScrollRight}
			/>
		</section>
	);
}
